import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuid } from 'uuid';
import * as backendConstants from '../constants/backendConstants';
import { retryOnError } from '../tools/retryUtils';
import {
  getDocumentIdField,
  set,
  setMerge,
  executeQuery,
  firestoreCollection,
  firestoreDocumentById,
  createNewId,
} from '../firebase/firestore';
import { averageField } from '../tools/arrayUtils';
import { fetchImagesForDocument } from './documentImageHelper';
import getKitapoService from '../services/KitapoService';

const IGNORE_ZERO_FIELD = true;
const MAX_CHUNK_SIZE = 8;

const LOG_ERROR = false;

const logError = (message, error) => {
  if (!LOG_ERROR) return;

  console.error(message, error);
};

export const loadAnalysesIndividually = async (chunk, parentId) => {
  const cleanAnalyses = [];
  await Promise.all(chunk.map(analysisId => firestoreDocumentById('analyses', analysisId).get())
    .map(async (currentAnalysisPromise, index) => {
      try {
        cleanAnalyses.push(await currentAnalysisPromise);
      } catch (e) {
        logError(`[loadAnalysesIndividually] Analysis failed to load ${chunk[index]} with parent: ${parentId}, error`, e);
      }
    }, []));
  return cleanAnalyses;
};

export const loadAnalysesChunk = async (chunk, parentId) => {
  try {
    return await firestoreCollection('analyses')
      .where(getDocumentIdField(), 'in', chunk)
      .get();
  } catch (e) {
    logError('Failed to load chunk', parentId, chunk, e);
    return { docs: await loadAnalysesIndividually(chunk, parentId) };
  }
};

export async function getAnalysesFromIds(userId, displayName, analysisIds, parentId) {
  const analysesHeadersSnapshots = await Promise.all(arrayChunks(analysisIds, MAX_CHUNK_SIZE)
    .map(chunk => loadAnalysesChunk(chunk, parentId)));

  const analysesWithUserAnalyses = await Promise.all(
    analysesHeadersSnapshots
      .flatMap(header => header.docs)
      .map(async (headerSnapshot) => {
        let userAnalysesSnapshot;
        let commentsSnapshot = [];
        let comments = [];

        try {
          userAnalysesSnapshot = await headerSnapshot.ref.collection('userAnalyses').get();
          commentsSnapshot = await headerSnapshot.ref.collection('analysisComments').get();
          comments = get(commentsSnapshot, 'docs', []).map(snapshot => snapshot.data());
        } catch (e) {
          console.warn('***** Failed to load analysis: ', headerSnapshot.id, e);
        }

        return {
          ...headerSnapshot.data(),
          id: headerSnapshot.id,
          userAnalyses: userAnalysesSnapshot === undefined
            ? []
            : userAnalysesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          comments,
        };
      }),
  );

  return prepareHeaderFromUserAnalyses(userId, displayName, analysesWithUserAnalyses);
}

const arrayChunks = (array, chunkSize) =>
  Array(Math.ceil(array.length / chunkSize))
    .fill()
    .map((_, index) => index * chunkSize)
    .map(begin => array.slice(begin, begin + chunkSize));

const getActiveUsers = (analysis) => analysis.userAnalyses.map((userAnalysis) => userAnalysis.id);

const getActivityCount = (analysis) =>
  analysis.userAnalyses.reduce((currentCount, userAnalysis) => currentCount
    + get(userAnalysis, 'evaluation.significantElements', []).filter(element => !element.archived).length
    + get(userAnalysis, 'reflection.comments', []).filter(comment => !comment.archived).length, 0)
  + (analysis.comments || []).filter(comment => !comment.archived).length;

export const getCurrentUserAnalysis = (userId, userAnalyses) =>
  userAnalyses.findIndex(userAnalysis => userAnalysis.id === userId);

export async function prepareHeaderFromUserAnalyses(userId, userDisplayName, analysesWithUserAnalyses) {
  return Promise.all(analysesWithUserAnalyses.map(async (currentAnalysis) => {
    const { url, leadImageUrl, webViewableUrl } = await fetchImagesForDocument(currentAnalysis.documentDetails);
    // currentAnalysis is modified in this function and should be cloned.
    // But it brakes the analysis UI soooo :/
    const result = {
      userAnalyses: [],
      ...currentAnalysis,
      documentDetails: {
        ...currentAnalysis.documentDetails,
        documentScore: averageField(currentAnalysis.userAnalyses, 'evaluation.documentScore', IGNORE_ZERO_FIELD),
        activeUsers: getActiveUsers(currentAnalysis),
        activityCount: getActivityCount(currentAnalysis),
        url,
        leadImageUrl,
        webViewableUrl,
      },
    };
    if (result.documentDetails.url) {
      result.computedUrl = currentAnalysis.documentDetails.url;
    } else if (url) {
      result.computedUrl = result.documentDetails.url;
    }
    if (url) {
      result.documentDetails.url = url;
    }

    if (leadImageUrl) {
      result.documentDetails.leadImageUrl = leadImageUrl;
    }

    if (getCurrentUserAnalysis(userId, result.userAnalyses) < 0) {
      result.userAnalyses.push(getNewUserAnalysisBody(userId, userDisplayName));
    }
    if (result.acl) {
      const acls = new Map();
      result.acl.forEach(acl => {
        acls.set(acl.entityId, acl);
      });
      result.acl = Array.from(acls.values());
    }
    return result;
  }));
}

// TODO : old one, there's a new one below... (getDefaultCurrentUserAnalysis) Should harmonize
export const getNewUserAnalysisBody = (userId, displayName) => ({
  id: userId,
  displayName,
  evaluation: {
    documentScore: 0,
    scores: [
      { key: 'validityCredibility', value: 0 },
      { key: 'relevanceUsefulness', value: 0 },
      { key: 'noveltyComplementarity', value: 0 },
      { key: 'resolution', value: 0 },
      { key: 'feasibility', value: 0 },
      { key: 'mobilisation', value: 0 },
      { key: 'operationalization', value: 0 },
      { key: 'learningProcessImprovement', value: 0 },
      { key: 'criticalUrgency', value: 0 },
    ],
    significantElements: [],
    id: uuid(),
  },
  reflection: { comments: [] },
});

// TODO : see if old one above is still used, harmonize...
export function getDefaultUserAnalysis({ userId, displayName }) {
  return {
    documentScore: 0,
    id: userId,
    displayName,
    evaluation: {
      significantElements: [],
      scores: [],
    },
    reflection: {
      comments: [],
    },
  };
}

export function createNewAnalysis(noosDocument, userId, userDisplayName, aclObject) {
  const newAnalysis = {
    id: createNewId(),
    acl: [aclObject],
    createdBy: userId,
    documentDetails: {
      contentType: noosDocument.contentType,
      contentUrl: noosDocument.contentUrl,
      datePublished: noosDocument.datePublished,
      excerpt: noosDocument.excerpt,
      leadImageUrl: noosDocument.leadImageUrl,
      title: noosDocument.title,
      url: noosDocument.url,
      sourceId: noosDocument.sourceId,
      activeUsers: [],
      activityCount: 0,
      documentScore: 0,
    },
    documentId: noosDocument.documentId,
    storageId: noosDocument.storageId,
    userAnalyses: [getNewUserAnalysisBody(userId, userDisplayName)],
  };
  if (noosDocument.content) {
    newAnalysis.documentDetails.content = noosDocument.content;
  }
  if (noosDocument.wordCount) {
    newAnalysis.documentDetails.wordCount = noosDocument.wordCount;
  }

  return newAnalysis;
}

export const saveAnalysis = async (id, analysis, portfolioIds) => {
  try {
    const service = getKitapoService();
    if (portfolioIds) {
      await service.saveAnalysisV2(portfolioIds, analysis);
    } else {
      await service.saveAnalysis(analysis);
    }
  } catch (e) {
    console.log(`Error saveAnalysis for analysis ${id}`, e);
    throw e;
  }
};

export const addAnalysisToPortfolio = async (id, portfolioId) => {
  try {
    const service = getKitapoService();
    await service.addToPortfolio(portfolioId, id);
  } catch (e) {
    console.log(`Error addAnalysisToPortfolio for analysis ${id}`, e);
  }
};

export const removeAnalysisFromPortfolio = async (id, portfolioId) => {
  try {
    const service = getKitapoService();
    await service.removeFromPortfolio(portfolioId, id);
  } catch (e) {
    console.log(`Error removeAnalysisToPortfolio for analysis ${id}`, e);
    throw e;
  }
};

export const saveUserAnalysis = async (userId, analysisId, userAnalysis) => {
  console.log('saveUserAnalysis user Id, analysisId, userAnalysis', userId, analysisId, userAnalysis);
  const func = async () => {
    try {
      const userAnalysisCopy = cloneDeep(userAnalysis);
      const ref = firestoreDocumentById(
        `${backendConstants.FIRESTORE_COLLECTION_ANALYSES}/${analysisId}/${backendConstants.FIRESTORE_SUBCOLLECTION_USER_ANALYSES}`, // eslint-disable-line
        userId,
      );

      await set(
        ref,
        userAnalysisCopy,
      );

      return ref;
    } catch (e) {
      console.log('Error saveUserAnalysis', e);
      throw e;
    }
  };
  // Sometime it fails when the analysis is just being created and the acl denormalization is not
  // ready yet.
  await retryOnError(func, { maxRetry: 10 });
};

export const saveThreadComment = (analysisId, comment) => setMerge(
  firestoreDocumentById(
    `${backendConstants.FIRESTORE_COLLECTION_ANALYSES}/${analysisId}/${backendConstants.FIRESTORE_SUBCOLLECTION_COMMENTS}`,
    comment.id,
  ),
  comment,
);

export const saveThreadCommentVote = async (analysisId, commentId, userId, upVoteNewValue) => {
  // TODO constant for votes subcol
  set(
    firestoreDocumentById(
      `${backendConstants.FIRESTORE_COLLECTION_ANALYSES}/${analysisId}/`
      + `${backendConstants.FIRESTORE_SUBCOLLECTION_COMMENTS}/${commentId}/votes`,
      userId,
    ),
    {
      id: userId,
      upVoteValue: upVoteNewValue,
    },
  );
};

const findAnalysisForDocument = async (documentId, aclObject) => {
  const portfolioDoc = await findPortfolioForAcl(aclObject);
  return findAnalysisIdForPortfolio(portfolioDoc, documentId);
};

const findAnalysisIdForPortfolio = async (portfolioDoc, documentId) => {
  const isDocumentIdInPortfolio = portfolioDoc.documentIds.includes(documentId);
  if (!isDocumentIdInPortfolio) return undefined;

  const analyses = await Promise.all(
    portfolioDoc.analysisIds.map(async (analysisId) => {
      try {
        return await executeQuery(firestoreDocumentById(backendConstants.FIRESTORE_COLLECTION_ANALYSES, analysisId));
      } catch (error) {
        logError(`Can't fetch analysis ${analysisId}`, error);
        return undefined;
      }
    }),
  );

  const analysesForDocument = analyses
    .filter(analysisId => analysisId !== undefined)
    .filter(analysis => analysis.documentDetails.documentId === documentId);

  if (analysesForDocument.length === 0) return undefined;

  return analysesForDocument[0];
};

const findPortfolioForAcl = (aclObject) => {
  if (aclObject.entityType === 'Group' || aclObject.entityType === 'group') {
    return executeQuery(
      firestoreDocumentById(
        backendConstants.FIRESTORE_COLLECTION_GROUP_PORTFOLIOS,
        aclObject.entityId,
      ),
    );
  }
  if (aclObject.entityType === 'User' || aclObject.entityType === 'user') {
    return executeQuery(
      firestoreDocumentById(
        backendConstants.FIRESTORE_COLLECTION_USER_PORTFOLIOS,
        aclObject.entityId,
      ),
    );
  }

  throw Error('Invalid acl object', aclObject);
};

export const getOrCreateAnalysisForDocument = async (document, userId, userDisplayName, aclObject) => {
  console.log('getOrCreateAnalysisForDocument', document, aclObject);
  if (document.id) return document.id;

  try {
    const analysis = await findAnalysisForDocument(document.documentId, aclObject);
    if (analysis) {
      console.log(`Found an analysis from ${userId} for document`, document, analysis);
      return { createdNew: false, analysis };
    }
    console.log(`Did not find an analysis from ${userId} for document`, document);
  } catch (e) {
    logError(e);
    throw e;
  }

  const newAnalysis = createNewAnalysis(document.documentDetails, userId, userDisplayName, aclObject);
  try {
    await saveAnalysis(newAnalysis.id, newAnalysis);
  } catch (e) {
    logError('saveAnalysis failed', e);
    throw e;
  }
  return { createdNew: true, analysis: newAnalysis };
};

export const buildAclObject = (entityType, entityId, displayName, permission = 'Owner') => ({
  displayName,
  entityId,
  entityType,
  permission,
});

export const isAnalysisInGroup = (state) => (groupId, analysisId, documentId) => {
  const groupPortfolio = state.groupPortfolios[groupId] || {};
  if (analysisId) {
    return Array.isArray(groupPortfolio.analysisIds) && groupPortfolio.analysisIds.includes(analysisId);
  }

  if (Array.isArray(groupPortfolio.documentIds)) {
    return groupPortfolio.documentIds.includes(documentId);
  }

  return false;
};

export const isAnalysisInPersonalPortfolio = (state) => async (userId, analysisId, documentId) => {
  const { portfolio } = state;

  // userPortfolio IS loaded, check directly in it
  if (portfolio && portfolio.analysisIds && portfolio.documentIds) {
    return (portfolio.analysisIds.includes(analysisId) && portfolio.documentIds.includes(documentId));
  }

  // userPortfolio is NOT loaded, we are in the extension, query the userPortfolios collection
  const ref = firestoreDocumentById(backendConstants.FIRESTORE_COLLECTION_USER_PORTFOLIOS, userId);
  try {
    const snap = await ref.get();
    const { analysisIds = [], documentIds = [] } = snap.data();

    return analysisIds.includes(analysisId) && documentIds.includes(documentId);
  } catch (e) {
    logError('A problem occurred while trying to access the userPortfolios collection: ', e);
    return false;
  }
};
